import {
  AuthenticationRefresher,
  ValidAuthentication
} from '@/app/platform/platform-core/user/services/types';
import { AsyncResult } from '@common/result/asyncResult';
import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { PlatformApiResultOfUserInfoDto } from '@data/_generated-api/platform-api/models/platform-api-result-of-user-info-dto';
import Morphism from 'morphism';
import { UserEgiApp } from '@/app/platform/platform-core/user/models/user-egi-app';
import { PlatformUser } from '@/app/platform/platform-core/user/models/platform-user';
import { err, ok, Result } from '@common/result/Result';
import { UpamErrorMapper } from '@data/_generated-api/error-mappers/upam-error-mapper';
import { NavigationItemDataEnricher } from '@/app/platform/platform-shared/enrichers/navigation-item-data-enricher';
import { environment } from '@/environments/environment';
import { currentTab } from '@/app/current-tab';
import { ErrorInformation } from '@shared/models/error/error-information';
import { EXPECTED_API_ERRORS } from '@data/_generated-api/http-interceptors/silent-api-errors';

/**
 * De backend roteert de refresh-cookie bij elke refresh-call; twee bijna-gelijktijdige
 * calls met dezelfde cookie loggen de verliezer dus uit. Daarom wordt hier op drie
 * niveaus ontdubbeld: één refresh tegelijk binnen de tab (single-flight promise), één
 * tab tegelijk over alle tabs heen (Web Lock) en hergebruik van auth die een andere
 * tab binnen het lopende interval al ophaalde (authSavedAt-venster).
 */
const REFRESH_LOCK_NAME = 'egitura-refresh-token';
/** Marge onder het refresh-interval; daarbinnen geldt opgeslagen auth als vers genoeg. */
const SAVED_AUTH_REUSE_MARGIN_MS = 5000;
/** Na deze wachttijd op de lock refreshen we zonder lock (bv. bevroren lock-houder). */
const REFRESH_LOCK_TIMEOUT_MS = 15000;
/**
 * Wachttijden vóór een nieuwe poging na een netwerkfout of 5xx. Een gsm die net terug uit de
 * achtergrond komt, heeft vaak heel even geen netwerk; zonder herhaling belandt een herladen
 * tab op de loginpagina en wacht een verlopen token een volle tick, terwijl de sessie nog
 * geldig is. Opnieuw proberen met dezelfde cookie is veilig: haalde de vorige poging de server
 * toch, dan levert de rotatie-graceperiode van de backend de vervangtoken op.
 */
const TRANSIENT_RETRY_DELAYS_MS: readonly number[] = [1000, 3000];

@Injectable({
  providedIn: 'root'
})
export class JwtAuthenticationRefresher implements AuthenticationRefresher {
  private pendingRefresh?: Promise<Result<ValidAuthentication, ErrorInformation>>;
  /** Overschrijfbaar in specs, zodat die niet echt hoeven te wachten. */
  protected transientRetryDelaysMs: readonly number[] = TRANSIENT_RETRY_DELAYS_MS;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly navigationItemEnricher: NavigationItemDataEnricher,
    private readonly upamErrorMapper: UpamErrorMapper
  ) {}

  async refresh(): AsyncResult<ValidAuthentication> {
    if (!this.pendingRefresh) {
      this.pendingRefresh = this.refreshExclusively().finally(() => (this.pendingRefresh = undefined));
    }
    return this.pendingRefresh;
  }

  private async refreshExclusively(): AsyncResult<ValidAuthentication> {
    const refreshTask = (): AsyncResult<ValidAuthentication> => this.refreshOncePerInterval();

    const locks = navigator.locks;
    if (!locks?.request) {
      return refreshTask();
    }

    const lockRequest =
      typeof AbortSignal !== 'undefined' && 'timeout' in AbortSignal
        ? locks.request(REFRESH_LOCK_NAME, { signal: AbortSignal.timeout(REFRESH_LOCK_TIMEOUT_MS) }, refreshTask)
        : locks.request(REFRESH_LOCK_NAME, refreshTask);

    // Komt de lock niet vrij (bv. bevroren tab houdt hem vast), dan toch refreshen;
    // de rotatie-graceperiode van de backend vangt een eventuele dubbele rotatie op.
    // await i.p.v. .catch: lib.dom typeert request() als Promise<T> met T = de promise
    // van refreshTask, terwijl de browser die geneste promise gewoon uitpakt.
    try {
      return await lockRequest;
    } catch (error) {
      if (error instanceof DOMException && (error.name === 'AbortError' || error.name === 'TimeoutError')) {
        return refreshTask();
      }
      throw error;
    }
  }

  private async refreshOncePerInterval(): AsyncResult<ValidAuthentication> {
    if (currentTab.getUseMultipleTabs()) {
      const recentlySaved = this.useRecentlySavedAuthentication();
      if (recentlySaved) {
        return recentlySaved;
      }
    }

    return this.refreshByEndpointWithRetry();
  }

  /** Netwerk- en serverfouten krijgen nog enkele pogingen; daarna gaat de rejectie naar de caller. */
  private async refreshByEndpointWithRetry(): AsyncResult<ValidAuthentication> {
    for (const delayMs of this.transientRetryDelaysMs) {
      try {
        return await this.refreshByEndpoint();
      } catch (error) {
        if (!this.isRetryable(error)) {
          throw error;
        }
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    return this.refreshByEndpoint();
  }

  /**
   * Auth die een andere tab binnen het lopende refresh-interval al ophaalde volstaat:
   * de JWT daarin is hooguit één interval oud. Zo roteert per interval maar één tab de
   * cookie en overleeft de sessie ook een weggevallen "leidende" tab.
   */
  private useRecentlySavedAuthentication(): Result<ValidAuthentication, ErrorInformation> | undefined {
    const savedAt = currentTab.getAuthSavedAt();
    const reuseWindowMS = Math.max(
      environment.auth.refreshTokenIntervalMS - SAVED_AUTH_REUSE_MARGIN_MS,
      SAVED_AUTH_REUSE_MARGIN_MS
    );
    if (savedAt === undefined || Date.now() - savedAt >= reuseWindowMS) {
      return undefined;
    }

    const result = currentTab.getAuth();
    if (!result?.isValid) {
      return undefined;
    }

    return ok(this.mapResponseTOAuthenticationResponse(result));
  }

  private async refreshByEndpoint(): AsyncResult<ValidAuthentication> {
    // BELANGRIJK: EXPECTED_API_ERRORS niet weghalen. Zonder die vlag maakt UpamErrorInterceptor
    // van elke fout een gewone response met foutenvelope; bij een netwerkfout (status 0) is dat
    // een ProgressEvent zonder isValid. Hieronder leek dat een ongeldige sessie: de gebruiker werd
    // uitgelogd en kreeg de toast "Egitura Platform is offline!", terwijl de sessie op de server
    // nog geldig was. Op gsm gebeurde dat geregeld bij het terugkeren uit een andere app, zie
    // docs/SESSIES.md. Tests: jwt-authentication-refresher.interceptor-chain.spec.ts.
    const context = new HttpContext().set(EXPECTED_API_ERRORS, true);
    let result: PlatformApiResultOfUserInfoDto;
    try {
      result = await this.httpClient
        .post<PlatformApiResultOfUserInfoDto>(
          environment.apiPaths.egituraPlatformApi + environment.auth.refreshTokenRoute,
          {},
          { withCredentials: true, context }
        )
        .toPromise();
    } catch (error) {
      if (this.isAuthRejection(error)) {
        currentTab.clearAuth();
        const body = (error as HttpErrorResponse).error as PlatformApiResultOfUserInfoDto | undefined;
        const errors = this.upamErrorMapper.map(body?.errors ?? []);
        return err(errors);
      }
      // Transiënt (netwerk/5xx): rejectie doorgeven zodat de caller de sessie kan
      // behouden en het bij de volgende tick opnieuw kan proberen.
      throw error;
    }

    if (!result.isValid) {
      currentTab.clearAuth();
      const errors = this.upamErrorMapper.map(result.errors);
      return err(errors);
    }

    if (currentTab.getUseMultipleTabs()) {
      currentTab.saveAuth(result);
    }

    const authentication = this.mapResponseTOAuthenticationResponse(result);
    return ok(authentication);
  }

  /** Enkel een expliciete 401 betekent "sessie is echt ongeldig"; al de rest is transiënt. */
  private isAuthRejection(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 401;
  }

  /** Netwerkfout (status 0) of serverfout: kan bij een nieuwe poging wel lukken. */
  private isRetryable(error: unknown): boolean {
    return error instanceof HttpErrorResponse && (error.status === 0 || error.status >= 500);
  }

  private mapResponseTOAuthenticationResponse(
    result: PlatformApiResultOfUserInfoDto
  ): ValidAuthentication {
    const apps = Morphism.map(UserEgiApp, result.value.apps);
    const user = Morphism.map(PlatformUser, result.value);

    apps.forEach(a => this.navigationItemEnricher.enrichWithEgiPages(a));

    const authentication: ValidAuthentication = {
      apps,
      user,
      jwtToken: result.value.jwtToken
    };
    return authentication;
  }
}
